import { useMemo } from 'react';

import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';

import { isDefined } from '@hsp/utilities';

import { useFuzzyFilter, usePagingData, useTableStoreSelector, useTableStoreSet } from 'components/table/hooks';
import { ITableProps } from 'types/components/table';

export function useConfiguration<TData, TValue = never>({
  allowSortRemoval = false,
  columns,
  dataSource,
  enableExpanding = false,
  enableGlobalFilter = false,
  enablePaging = true,
  enableSorting = true,
  getRowCanExpand,
  getRowId,
}: ITableProps<TData, TValue>) {
  const globalFilter = useTableStoreSelector((state) => state.globalFilter);
  const sorting = useTableStoreSelector((state) => state.sorting);
  const setStore = useTableStoreSet();

  const { data, manualPagination, page, pageCount, pageSize, sort, total } = usePagingData(dataSource);
  const fuzzyFilter = useFuzzyFilter<TData>();

  let options: TableOptions<TData> = {
    columns,
    data,
    enableMultiRemove: true,
    enableSorting,
    enableSortingRemoval: allowSortRemoval,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: isDefined(getRowId) ? getRowId : (_, index) => index.toString(),
    globalFilterFn: fuzzyFilter,
    onPaginationChange: () => setStore({ page, pageSize }),
    onSortingChange: (e) => {
      if (typeof e === 'function') {
        const newSort = e(sorting ?? []);

        setStore({ sorting: newSort });
      }
    },
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
      sorting,
    },
  };

  if (manualPagination) {
    options = {
      ...options,
      manualFiltering: true,
      manualPagination,
      manualSorting: true,
      pageCount,
    };
  } else {
    options = {
      ...options,
      getPaginationRowModel: enablePaging ? getPaginationRowModel() : undefined,
      getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    };
  }

  if (enableGlobalFilter && !manualPagination) {
    options = {
      ...options,
      getFilteredRowModel: getFilteredRowModel(),
      onGlobalFilterChange: () => {
        const { rows } = table.getFilteredRowModel();

        setStore({ total: rows.length });
      },
      state: {
        ...options.state,
        globalFilter,
      },
    };
  }

  if (enableExpanding) {
    options = {
      ...options,
      autoResetExpanded: true,
      getExpandedRowModel: getExpandedRowModel(),
      getRowCanExpand: isDefined(getRowCanExpand) ? getRowCanExpand : () => true,
    };
  }

  const table = useReactTable(options);

  return useMemo(
    () =>
      ({
        sort,
        table,
        total,
      }) as const,
    [sort, table, total]
  );
}
